import React from 'react';
import {Image, Page, Text, View} from '@react-pdf/renderer';
import {IntlShape} from 'react-intl';
import {companyLogoSrc} from '../../../../directApi';
import {formatQuoteDate} from '../quoteDocument';
import {stylesFor} from './quotePdfStyles';
import type {QuoteDocumentData, QuoteTemplate} from '../resolver/types';

type QuoteCoverPageProps = {
	data: QuoteDocumentData;
	template: QuoteTemplate;
	intl: IntlShape;
};

/**
 * The cover: a full-bleed band carrying the logo, the kicker, the project title and who the quote
 * is for, over a two-column grid of the quote's facts.
 *
 * The band runs to the sheet's top and side edges, so this page carries no page padding and the
 * band and the body below it supply their own. It also renders no running header — the band is the
 * top of the page.
 */
const QuoteCoverPage = ({data, template, intl}: QuoteCoverPageProps): JSX.Element => {
	const styles = stylesFor(template);
	const logoSrc = template.branding.showLogo && data.companyLogoId ? companyLogoSrc(data.companyLogoId) : null;
	const showWordmark = template.branding.showLogo && !logoSrc;

	const metaCells: Array<{label: string; value: string}> = [
		{
			label: intl.formatMessage({id: 'quote_designer.cover.project'}),
			value: `${data.projectIndicator} · ${data.projectName}`,
		},
		{label: intl.formatMessage({id: 'quote_designer.cover.version'}), value: `v${data.revision}`},
		{label: intl.formatMessage({id: 'quote_designer.cover.prepared_by'}), value: data.companyName},
		...(data.clientName
			? [{label: intl.formatMessage({id: 'quote_designer.cover.prepared_for'}), value: data.clientName}]
			: []),
		{
			label: intl.formatMessage({id: 'quote_designer.cover.quote_date'}),
			value: formatQuoteDate(data.issuedDate, intl),
		},
		{
			label: intl.formatMessage({id: 'quote_designer.cover.valid_until'}),
			value: formatQuoteDate(data.expiryDate, intl),
		},
	];

	return (
		<Page size="A4" style={styles.coverPage}>
			<View style={styles.coverContent}>
				<View style={styles.coverBand}>
					<View>
						{logoSrc ? <Image src={{uri: logoSrc, credentials: 'include'}} style={styles.coverLogoImage} /> : null}
						{showWordmark ? <Text style={styles.coverLogoWord}>{data.companyName}</Text> : null}
					</View>
					<View style={styles.coverTitleBlock}>
						<Text style={styles.coverEyebrow}>{intl.formatMessage({id: 'quote_designer.pdf.eyebrow'})}</Text>
						<Text style={styles.coverTitle}>{data.projectName}</Text>
						{data.clientName ? (
							<Text style={styles.coverFor}>
								{intl.formatMessage({id: 'quote_designer.pdf.prepared_for_client'}, {client: data.clientName})}
							</Text>
						) : null}
					</View>
				</View>

				<View style={styles.coverBody}>
					<View style={styles.coverMeta}>
						{metaCells.map(cell => (
							<View key={cell.label} style={styles.coverMetaCell}>
								<Text style={styles.coverMetaLabel}>{cell.label}</Text>
								<Text style={styles.coverMetaValue}>{cell.value}</Text>
							</View>
						))}
					</View>
				</View>
			</View>

			{template.layout.pageFooter && (
				<View fixed style={styles.foot}>
					<Text style={styles.footText}>{data.companyName}</Text>
					<Text
						style={styles.footText}
						render={({pageNumber, totalPages}) =>
							intl.formatMessage({id: 'quote_designer.pdf.page_of'}, {page: pageNumber, total: totalPages})
						}
					/>
				</View>
			)}
		</Page>
	);
};

export default QuoteCoverPage;
